import { forwardRef, memo, useImperativeHandle, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';

import * as typings from '@typings';
import { toast } from '@features';
import {
  downloadYamlConfig,
  getResolverSchema,
  getSchemaDefaultValues,
} from '@utils';

import { Theme } from '@components';
import { SchemaProvider } from '@components/Providers';
import { Button, Icon, Modal, Render, Tooltip } from '@components/Shared';

import { SchemaImportConfig } from './SchemaImportConfig';
import { SchemaLogo } from './SchemaLogo';
import { SchemaNode } from './SchemaNode';

type FormSchema = z.infer<ReturnType<typeof getResolverSchema>>;

export type SchemaRef = {
  methods: ReturnType<typeof useForm>;
};

type Props = {
  schema: typings.Schema.Model[];
  name: string;
  version: string;
  title?: string;
  loading?: boolean;
  hideFooter?: boolean;
  logo?: typings.ImageUrl;
  onSubmit?: (data: FormSchema) => void;
};

const SchemaWithRef = forwardRef<SchemaRef, Props>(
  (
    { schema, logo, title, name, version, loading, hideFooter, onSubmit },
    ref,
  ) => {
    const schemaResolver = useMemo(() => getResolverSchema(schema), [schema]);

    const defaultValues = useMemo(
      () => getSchemaDefaultValues(schema),
      [schema],
    );

    const methods = useForm<FormSchema>({
      resolver: zodResolver(schemaResolver),
      defaultValues,
    });

    useImperativeHandle(ref, () => ({
      methods,
    }));

    const handleSubmit = methods.handleSubmit((fields) => {
      onSubmit?.(fields);
    });

    const handleInputParamsDownload = async () => {
      const isValid = await methods?.trigger();

      if (!isValid) {
        toast.error('Please fill in all required fields');
        return;
      }

      const { displayName, ...others } = methods?.getValues() ?? {};

      downloadYamlConfig(
        {
          display_name: displayName,
          template_name: name,
          template_version: version,
          input: others,
        },
        `${name}-${version}-config`,
      );
    };

    return (
      <Theme.Container className="relative mx-auto flex w-full max-w-[720px] flex-col p-0">
        <SchemaProvider reset={methods?.reset}>
          <Render if={title && version}>
            <div className="flex items-center gap-4 border-b border-neural-02 px-10 py-7">
              <SchemaLogo src={logo} />

              <div className="flex flex-1 flex-col">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-h4">{title}</h3>

                  <Render if={name && version}>
                    <SchemaImportConfig
                      templateName={name}
                      templateVersion={version}
                      schema={schemaResolver.omit({ displayName: true })}
                    />
                  </Render>
                </div>

                <p className="-mt-2 text-neural-04">Version: {version}</p>
              </div>
            </div>
          </Render>

          <FormProvider {...methods}>
            <form noValidate className="flex flex-1 flex-col gap-12 px-10 py-7">
              <div className="flex flex-col gap-6">
                {schema.map((model) => (
                  <SchemaNode key={model.name} isTopLevelNode schema={model} />
                ))}
              </div>
            </form>
          </FormProvider>

          <Render if={!hideFooter}>
            <Modal.Footer
              sticky
              className="mt-auto items-center rounded-b-2xl px-10 py-8"
            >
              <Tooltip>
                <div slot="trigger">
                  <Button
                    variant="secondary"
                    disabled={!methods.formState.isValid}
                    onClick={handleInputParamsDownload}
                    className="px-4"
                    aria-label="Download parameters"
                  >
                    <Icon icon={faDownload} className="text-[20px]" />
                  </Button>
                </div>
                Export App Configuration
              </Tooltip>

              <Button
                loading={loading}
                className="px-10 capitalize"
                onClick={handleSubmit}
              >
                Install
              </Button>
            </Modal.Footer>
          </Render>
        </SchemaProvider>
      </Theme.Container>
    );
  },
);

export const Schema = memo(SchemaWithRef);

Schema.displayName = 'Schema';
